import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal } from "./anim";
import ParallaxImage from "./ParallaxImage";
import { CTAButton, Eyebrow } from "./ui-bits";

export default function EventPageTemplate({
  eyebrow,
  titleLines,
  intro,
  image,
  imageAlt,
  occasionsTitle,
  occasions,
  paragraphs = [],
  cta,
  ctaTo = "/contatti",
  selfNote = true,
  preventivoCta = false,
}) {
  return (
    <article data-testid="event-page">
      <section className="pt-32 md:pt-44 pb-12 md:pb-16">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>{eyebrow}</Eyebrow>
          <MaskLines
            lines={titleLines}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1.02] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/65">{intro}</p>
          </Reveal>
        </div>
      </section>

      <Reveal>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <ParallaxImage
            src={image}
            alt={imageAlt}
            speed={0.1}
            className="relative h-[46vh] md:h-[62vh] rounded-3xl"
          />
          {selfNote && (
            <p className="mt-6 text-xs text-white/40 italic">
              Immagine illustrativa del concept Sprizziamoci.
            </p>
          )}
        </div>
      </Reveal>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 md:grid-cols-12">
          {occasions?.length ? (
            <div className="md:col-span-5">
              <Reveal>
                <h3 className="font-display text-3xl md:text-4xl font-semibold mb-8">
                  {occasionsTitle || "Sprizziamoci per:"}
                </h3>
                <ul className="space-y-0">
                  {occasions.map((o, i) => (
                    <li
                      key={i}
                      className="flex items-baseline gap-5 border-b border-white/[0.07] py-4 group"
                    >
                      <span className="font-display italic text-brand-orange text-lg">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm md:text-base text-white/75 group-hover:text-white transition-colors duration-200">
                        {o}
                      </span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          ) : null}

          <div className={`space-y-6 ${occasions?.length ? "md:col-span-6 md:col-start-7" : "md:col-span-8"}`}>
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-base md:text-lg leading-relaxed text-white/65">{p}</p>
              </Reveal>
            ))}
            {selfNote && (
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.05] p-6">
                  <p className="text-sm leading-relaxed text-brand-cream/80">
                    <span className="font-semibold text-brand-orange">Self service, vero Sprizziamoci.</span>{" "}
                    I tuoi ospiti si servono da soli. Spina, ghiaccio e bicchieri sempre pronti.
                  </p>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-10 md:p-14">
              <div>
                <p className="eyebrow mb-3">Pronto a festeggiare?</p>
                <p className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-xl">
                  Raccontaci il tuo evento: costruiamo il servizio insieme.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                {ctaTo.startsWith("#") ? (
                  <a
                    href={ctaTo}
                    data-testid="event-page-cta"
                    className="group inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-[11px] md:text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 bg-brand-orange text-brand-ink hover:bg-[#ff9433] shadow-[0_0_40px_rgba(255,121,0,0.25)]"
                  >
                    <span>{cta}</span>
                    <ArrowRight
                      size={15}
                      strokeWidth={2.4}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </a>
                ) : (
                  <CTAButton to={ctaTo} testId="event-page-cta">
                    {cta}
                  </CTAButton>
                )}
                {preventivoCta && (
                  <CTAButton to="/preventivo" variant="outline" testId="event-page-preventivo-cta">
                    Calcola il preventivo online
                  </CTAButton>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
